import { Component, Inject, OnInit } from '@angular/core';
import { Client } from '@shared/models';
import { getInitials } from '@shared/utils';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ClientsService } from '@shared/services';
import { BreakpointService } from '../../../services/breakpoint/breakpoint.service';

@Component({
  standalone: false,
  selector: 'migo-apps-serach-client-dialog',
  templateUrl: './search-client-dialog.component.html',
  styleUrl: './search-client-dialog.component.scss',
})
export class SearchClientDialogComponent implements OnInit {
  clients: Client[] = [];
  originalClients: Client[] = [];
  searchForm!: FormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { clients: Client[] },
    private dialogRef: MatDialogRef<SearchClientDialogComponent>,
    private builder: FormBuilder,
    private readonly clientsSvc: ClientsService,
    public bp: BreakpointService
  ) {}

  ngOnInit(): void {
    this.createForm();
    this.setupSearchFormListener();
  }

  private createForm() {
    this.searchForm = this.builder.group({
      searchField: [''],
    });
  }

  private setupSearchFormListener() {
    this.originalClients = this.data.clients;
    this.clients = [...this.originalClients];

    this.searchForm
      .get('searchField')
      ?.valueChanges.pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((query) => {
        this.clients = this.searchClients(query, this.originalClients);
      });
  }

  getInitials(value: string): string {
    return getInitials(value).toUpperCase();
  }

  private searchClients(query: string, clients: Client[]): Client[] {
    if (!query.trim()) return [...clients];

    const lowerQuery = query.toLowerCase();

    return clients
      .map((client) => ({
        client,
        exactMatch: client.name.toLowerCase() === lowerQuery,
        startsWith: client.name.toLowerCase().startsWith(lowerQuery),
        includes: client.name.toLowerCase().includes(lowerQuery),
      }))
      .filter(
        ({ exactMatch, startsWith, includes }) =>
          exactMatch || startsWith || includes
      )
      .sort((a, b) => {
        return (
          (b.exactMatch ? 3 : b.startsWith ? 2 : b.includes ? 1 : 0) -
          (a.exactMatch ? 3 : a.startsWith ? 2 : a.includes ? 1 : 0)
        );
      })
      .map(({ client }) => client);
  }

  selectClient(client: Client) {
    this.clientsSvc.selectClient(client);
    this.close();
  }

  close() {
    this.dialogRef.close();
  }
}
